import axios from 'axios'

// Use relative URLs since we're consistently proxying in both dev and production
const API_BASE_URL = '/api'

// Create axios instance with base configuration
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    'Pragma': 'no-cache',
  },
})

// Request interceptor for logging
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`)

    // Add cache-busting parameter to prevent 304 responses
    const timestamp = Date.now();
    const separator = config.url.includes('?') ? '&' : '?';
    config.url = `${config.url}${separator}_t=${timestamp}`;

    return config
  },
  (error) => {
    console.error('API Request Error:', error)
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    console.error('API Response Error:', error.response?.data || error.message)

    // Handle 401 Unauthorized - token expired or invalid
    if (error.response?.status === 401) {
      // Clear any stale auth state and redirect to login
      // Avoid redirect loops by checking current path
      if (!window.location.pathname.includes('/login') && !window.location.pathname.includes('/auth')) {
        console.log('Session expired, redirecting to login...')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

// User API
export const userAPI = {
  getUsers: () => api.get('/users'),
  getUserById: (id) => api.get(`/users/${id}`),
  createUser: (userData) => api.post('/users', userData),
  updateUser: (id, userData) => api.put(`/users/${id}`, userData),
  deleteUser: (id) => api.delete(`/users/${id}`),
  clearUserData: (userId) => api.post(`/clear-user-data/${userId}`),
}

// Journey Milestones API
export const journeyMilestonesAPI = {
  getMilestones: () => api.get('/journey-milestones'),
  getMilestoneById: (id) => api.get(`/journey-milestones/${id}`),
  createMilestone: (milestoneData) => api.post('/journey-milestones', milestoneData),
  updateMilestone: (id, milestoneData) => api.put(`/journey-milestones/${id}`, milestoneData),
  deleteMilestone: (id) => api.delete(`/journey-milestones/${id}`),
  getMilestonesByUserId: (userId) => api.get(`/journey-milestones/user/${userId}`),
}

// Interventions API
export const interventionsAPI = {
  getInterventions: () => api.get('/interventions'),
  getInterventionsWithLinks: () => api.get('/interventions/with-links'),
  getInterventionById: (id) => api.get(`/interventions/${id}`),
  createIntervention: (interventionData) => api.post('/interventions', interventionData),
  updateIntervention: (id, interventionData) => api.put(`/interventions/${id}`, interventionData),
  deleteIntervention: (id) => api.delete(`/interventions/${id}`),
  getInterventionsByDiagnosis: (code) => api.get(`/interventions/diagnosis/${code}`),
  getInterventionsByTreatment: (code) => api.get(`/interventions/treatment/${code}`),
  getInterventionsByTypeCodes: (diagnosisCode, treatmentCode) =>
    api.get(`/interventions/types/${diagnosisCode || ''}/${treatmentCode || ''}`),
  getInterventionDays: (interventionId) => api.get(`/interventions/${interventionId}/days`),
  updateInterventionDay: (interventionId, day, dayData) => api.put(`/interventions/${interventionId}/days/${day}`, dayData),
}

// Session Debug API
export const sessionDebugAPI = {
  getRecentSessions: () => api.get('/session-debug/recent-sessions'),
  getDebugValues: (sessionId) => api.get(`/session-debug/${sessionId}`),
  setDebugValues: (sessionId, debugValues) => api.post(`/session-debug/${sessionId}`, { debug_values: debugValues }),
  deleteDebugValues: (sessionId) => api.delete(`/session-debug/${sessionId}`),
}

// Current Time API
export const currentTimeAPI = {
  getCurrentTime: (sessionToken = null) => {
    const headers = sessionToken ? { 'asta_session_token': sessionToken } : {}
    return api.get('/current-time', { headers })
  },
}

// User Snapshot API
export const userSnapshotAPI = {
  getUserSnapshot: () => api.get('/user-snapshot'),
  getUserSnapshotById: (userId) => api.get(`/user-snapshot/${userId}`),
}

// Assessment Results API
export const assessmentResultsAPI = {
  getAssessmentResultById: (id) => api.get(`/assessment-results/${id}`),
  getAssessmentResultsByUserId: (userId) => api.get(`/assessment-results/user/${userId}`),
  getAssessmentResultsByInstrumentCode: (code) => api.get(`/assessment-results/instrument/${encodeURIComponent(code)}`),
  getScoreHistory: (userId, code = null, limit = 10) => {
    const params = new URLSearchParams()
    if (code) params.append('code', code)
    if (limit) params.append('limit', limit)
    return api.get(`/assessment-results/user/${userId}/history?${params}`)
  },
  deleteAssessmentResult: (id) => api.delete(`/assessment-results/${id}`),
}

// Assessment Instruments API
export const assessmentsAPI = {
  getAssessments: () => api.get('/assessment-instruments'),
  getAssessmentById: (id) => api.get(`/assessment-instruments/${id}`),
  getAssessmentByCode: (code) => api.get(`/assessment-instruments/code/${encodeURIComponent(code)}`),
}

// ===========================================
// NEW APIs for Intervention Configuration
// ===========================================

// Tracks API
export const tracksAPI = {
  getAll: (filters = {}) => api.get('/tracks', { params: filters }),
  getById: (id) => api.get(`/tracks/${id}`),
  create: (trackData) => api.post('/tracks', trackData),
  update: (id, trackData) => api.put(`/tracks/${id}`, trackData),
  delete: (id) => api.delete(`/tracks/${id}`),
  duplicate: (id, data = {}) => api.post(`/tracks/${id}/duplicate`, data),
}

// Track Types API (registry: code, label, placement flags, presentation defaults)
export const trackTypesAPI = {
  getAll: () => api.get('/track-types').then(res => res.data.types),
}

// Presentation API (lucide icon allowlist + copy limits for pathway display)
export const presentationAPI = {
  getIcons: () => api.get('/presentation/icons').then(res => res.data),
  getStarterResourceCategories: () =>
    api.get('/presentation/starter-resource-categories').then(res => res.data.resource_categories),
}

// Domains API
export const domainsAPI = {
  getAll: () => api.get('/domains'),
  getById: (id) => api.get(`/domains/${id}`),
  create: (domainData) => api.post('/domains', domainData),
  update: (id, domainData) => api.put(`/domains/${id}`, domainData),
  delete: (id) => api.delete(`/domains/${id}`),
  duplicate: (id, data = {}) => api.post(`/domains/${id}/duplicate`, data),
}

// Strategies API
export const strategiesAPI = {
  getAll: () => api.get('/strategies'),
  getById: (id) => api.get(`/strategies/${id}`),
  create: (strategyData) => api.post('/strategies', strategyData),
  update: (id, strategyData) => api.put(`/strategies/${id}`, strategyData),
  delete: (id) => api.delete(`/strategies/${id}`),
  reorder: (orderedIds) => api.put('/strategies/reorder', { ids: orderedIds }),
}

// Context Rules API (Patient context rules for updating risk factors, trends, properties)
export const contextRulesAPI = {
  getAll: () => api.get('/context-rules'),
  getById: (id) => api.get(`/context-rules/${id}`),
  create: (ruleData) => api.post('/context-rules', ruleData),
  update: (id, ruleData) => api.put(`/context-rules/${id}`, ruleData),
  delete: (id) => api.delete(`/context-rules/${id}`),
}

// Risk Codes API
export const riskCodesAPI = {
  getAll: () => api.get('/risk-codes'),
  getById: (id) => api.get(`/risk-codes/${id}`),
  create: (riskCodeData) => api.post('/risk-codes', riskCodeData),
  update: (id, riskCodeData) => api.put(`/risk-codes/${id}`, riskCodeData),
  delete: (id) => api.delete(`/risk-codes/${id}`),
  duplicate: (id, data = {}) => api.post(`/risk-codes/${id}/duplicate`, data),
}

// Activities API (read-only in the UI for now; content edited via API/SQL)
export const activitiesAPI = {
  getAll: (includeInactive = false) =>
    api.get(`/activities${includeInactive ? '?include_inactive=true' : ''}`),
  getByKey: (activityKey) =>
    api.get(`/activities/${activityKey}?include_items=true&include_inactive=true`),
  getUserState: (activityKey, userId) =>
    api.get(`/activities/${activityKey}/users/${userId}/state`),
  getUserNextItems: (activityKey, userId) =>
    api.get(`/activities/${activityKey}/users/${userId}/next-items?limit=10`),
  // Partial update of the global definition (name, description, entry_modes,
  // is_active, ui tile fields). Categories live per intervention, not here.
  update: (activityKey, data) => api.put(`/activities/${activityKey}`, data),
}

// Agent Prompts API
export const agentPromptsAPI = {
  getAll: () => api.get('/agent-prompts'),
  getByType: (agentType) => api.get(`/agent-prompts/${agentType}`),
  create: (promptData) => api.post('/agent-prompts', promptData),
  update: (id, promptData) => api.put(`/agent-prompts/${id}`, promptData),
  delete: (id) => api.delete(`/agent-prompts/${id}`),
  duplicate: (id, data = {}) => api.post(`/agent-prompts/${id}/duplicate`, data),
  createVariant: (agentType, variantData) => api.post(`/agent-prompts/${agentType}/variants`, variantData),
  previewMerged: (params) => api.post('/agent-prompts/preview', params),
}

// Intervention Configs API (Admin-only resource for program configurations)
export const interventionConfigsAPI = {
  getAll: (filters = {}) => api.get('/intervention-configs', { params: filters }).then(res => res.data),
  getById: (id) => api.get(`/intervention-configs/${id}`).then(res => res.data),
  create: (configData) => api.post('/intervention-configs', configData).then(res => res.data),
  update: (id, configData) => api.put(`/intervention-configs/${id}`, configData).then(res => res.data),
  delete: (id) => api.delete(`/intervention-configs/${id}`).then(res => res.data),
  duplicate: (id, data = {}) => api.post(`/intervention-configs/${id}/duplicate`, data).then(res => res.data),
  // Track (Pathway) associations
  getTracks: (id) => api.get(`/intervention-configs/${id}/tracks`).then(res => res.data),
  setTracks: (id, tracks) => api.put(`/intervention-configs/${id}/tracks`, tracks).then(res => res.data),
  // POST upserts: it also updates an existing association. Scheduling fields
  // fall back to server defaults when omitted, so always re-send them;
  // `custom_settings` and `presentation` are only replaced when sent
  // (server COALESCEs), so send them only when the user edited them.
  // Rejects with 409 { code: 'singleton_type_exists' } for a second
  // pathway of a singleton type.
  addTrack: (id, trackData) => api.post(`/intervention-configs/${id}/tracks`, trackData).then(res => res.data),
  updateTrack: (id, trackData) => api.post(`/intervention-configs/${id}/tracks`, trackData).then(res => res.data),
  removeTrack: (id, trackId) => api.delete(`/intervention-configs/${id}/tracks/${trackId}`).then(res => res.data),
  // Risk Code associations
  getRiskCodes: (id) => api.get(`/intervention-configs/${id}/risk-codes`).then(res => res.data),
  setRiskCodes: (id, riskCodes) => api.put(`/intervention-configs/${id}/risk-codes`, riskCodes).then(res => res.data),
  addRiskCode: (id, riskCodeData) => api.post(`/intervention-configs/${id}/risk-codes`, riskCodeData).then(res => res.data),
  removeRiskCode: (id, riskCodeId) => api.delete(`/intervention-configs/${id}/risk-codes/${riskCodeId}`).then(res => res.data),
  // Strategy associations
  getStrategies: (id) => api.get(`/intervention-configs/${id}/strategies`).then(res => res.data),
  getApplicableStrategies: (id) => api.get(`/intervention-configs/${id}/applicable-strategies`).then(res => res.data),
  setStrategies: (id, strategies) => api.put(`/intervention-configs/${id}/strategies`, strategies).then(res => res.data),
  addStrategy: (id, strategyData) => api.post(`/intervention-configs/${id}/strategies`, strategyData).then(res => res.data),
  removeStrategy: (id, strategyId) => api.delete(`/intervention-configs/${id}/strategies/${strategyId}`).then(res => res.data),
  // Context Rule associations
  getContextRules: (id) => api.get(`/intervention-configs/${id}/context-rules`).then(res => res.data),
  addContextRule: (id, ruleData) => api.post(`/intervention-configs/${id}/context-rules`, ruleData).then(res => res.data),
  removeContextRule: (id, ruleId) => api.delete(`/intervention-configs/${id}/context-rules/${ruleId}`).then(res => res.data),
  // Enrollments
  getEnrollments: (id, filters = {}) => api.get(`/intervention-configs/${id}/enrollments`, { params: filters }).then(res => res.data),
  enrollUser: (id, enrollmentData) => api.post(`/intervention-configs/${id}/enrollments`, enrollmentData).then(res => res.data),
  updateEnrollment: (id, enrollmentId, data) => api.put(`/intervention-configs/${id}/enrollments/${enrollmentId}`, data).then(res => res.data),
}

// Exercise picker categories, one list per intervention (plan §4).
// Rows: { id, intervention_config_id, slug, title, description, icon,
// position, enabled }. Errors carry { error, code } with codes such as
// duplicate_slug (409), invalid_slug, invalid_icon, invalid_order.
export const exerciseCategoriesAPI = {
  getAll: (interventionId) =>
    api.get(`/intervention-configs/${interventionId}/exercise-categories`).then(res => res.data),
  create: (interventionId, data) =>
    api.post(`/intervention-configs/${interventionId}/exercise-categories`, data).then(res => res.data),
  // slug is immutable; send title, description, icon, position, enabled.
  update: (interventionId, categoryId, data) =>
    api.put(`/intervention-configs/${interventionId}/exercise-categories/${categoryId}`, data).then(res => res.data),
  remove: (interventionId, categoryId) =>
    api.delete(`/intervention-configs/${interventionId}/exercise-categories/${categoryId}`).then(res => res.data),
  // Every category id exactly once, in display order; returns the new list.
  reorder: (interventionId, ids) =>
    api.put(`/intervention-configs/${interventionId}/exercise-categories/reorder`, { ids }).then(res => res.data),
}

// Activities assigned to an intervention, with their picker category.
// Rows: { assignment_id, activity_id, category_id, position, enabled,
// activity_key, name, description, entry_modes, ui, is_active, category }.
// Error codes: already_assigned (409), category_not_in_intervention,
// invalid_order, not_found (404).
export const interventionActivitiesAPI = {
  getAll: (interventionId) =>
    api.get(`/intervention-configs/${interventionId}/activities`).then(res => res.data),
  assign: (interventionId, data) =>
    api.post(`/intervention-configs/${interventionId}/activities`, data).then(res => res.data),
  // category_id (number or null), position, enabled
  update: (interventionId, activityId, data) =>
    api.put(`/intervention-configs/${interventionId}/activities/${activityId}`, data).then(res => res.data),
  unassign: (interventionId, activityId) =>
    api.delete(`/intervention-configs/${interventionId}/activities/${activityId}`).then(res => res.data),
  // Every assigned activity id exactly once, in display order; returns the new list.
  reorder: (interventionId, activityIds) =>
    api.put(`/intervention-configs/${interventionId}/activities/reorder`, { activity_ids: activityIds }).then(res => res.data),
}

// Patients API (Admin-only for patient enrollment management)
export const patientsAPI = {
  getEnrollments: (filters = {}) => api.get('/patients/enrollments', { params: filters }).then(res => res.data),
  getEnrollmentHistory: (userId) => api.get(`/patients/${userId}/enrollment-history`).then(res => res.data),
  getCurrentEnrollment: (userId) => api.get(`/patients/${userId}/enrollment`).then(res => res.data),
  assignToIntervention: (userId, data) => api.post(`/patients/${userId}/enrollment`, data).then(res => res.data),
  withdrawFromIntervention: (userId, reason = null) =>
    api.delete(`/patients/${userId}/enrollment`, { data: { withdrawal_reason: reason } }).then(res => res.data),
  changeIntervention: (userId, data) => api.put(`/patients/${userId}/enrollment`, data).then(res => res.data),
}

// Composition API (for preview and testing)
export const compositionAPI = {
  preview: (data) => api.post('/composition/preview', data),
  getContext: (userId) => api.get(`/composition/context/${userId}`),
  composeSession: (userId) => api.post(`/composition/session/${userId}`),
  getTodaySession: (userId) => api.get(`/composition/session/${userId}/today`),
  getAgenda: (agendaId) => api.get(`/composition/agenda/${agendaId}`),
  getStats: (userId) => api.get(`/composition/stats/${userId}`),
  getTrackStates: (userId) => api.get(`/composition/tracks/${userId}`).then(res => res.data.tracks),
  getRuleLog: (userId) => api.get(`/composition/rule-log/${userId}`).then(res => res.data.logs),
}

// Patient Therapeutic Model API
export const ptmAPI = {
  get: (userId) => api.get(`/ptm/${userId}`).then(res => res.data),
}

// ===========================================
// RBAC and System Administration APIs
// ===========================================

// Admin Users API (RBAC)
export const adminUsersAPI = {
  getMe: () => api.get('/admin-users/me').then(res => res.data),
  getAll: () => api.get('/admin-users').then(res => res.data),
  getById: (id) => api.get(`/admin-users/${id}`).then(res => res.data),
  create: (userData) => api.post('/admin-users', userData).then(res => res.data),
  update: (id, userData) => api.put(`/admin-users/${id}`, userData).then(res => res.data),
  delete: (id) => api.delete(`/admin-users/${id}`).then(res => res.data),
}

// Audit Log API
export const auditLogAPI = {
  getAll: (filters = {}) => api.get('/audit-log', { params: filters }).then(res => res.data),
  getById: (id) => api.get(`/audit-log/${id}`).then(res => res.data),
  getEntityHistory: (entityType, entityId) =>
    api.get(`/audit-log/entity/${entityType}/${entityId}`).then(res => res.data),
  getMyActivity: (limit = 50) =>
    api.get('/audit-log/my-activity', { params: { limit } }).then(res => res.data),
  getFilterOptions: () => api.get('/audit-log/filters/options').then(res => res.data),
}

// Assessment Instruments API (Instrument Library)
export const assessmentInstrumentsAPI = {
  getAll: (activeOnly = false) =>
    api.get('/assessment-instruments', { params: { active: activeOnly } }).then(res => res.data),
  getById: (id) => api.get(`/assessment-instruments/${id}`).then(res => res.data),
  getByCode: (code) => api.get(`/assessment-instruments/code/${code}`).then(res => res.data),
  create: (instrumentData) =>
    api.post('/assessment-instruments', instrumentData).then(res => res.data),
  update: (id, instrumentData) =>
    api.put(`/assessment-instruments/${id}`, instrumentData).then(res => res.data),
  delete: (id) => api.delete(`/assessment-instruments/${id}`).then(res => res.data),
  calculateScore: (code, responses) =>
    api.post(`/assessment-instruments/code/${code}/calculate`, { responses }).then(res => res.data),
}

// ===========================================
// Pathway Content Library APIs
// ===========================================

// Content Groups API
export const contentGroupsAPI = {
  getAll: (includeInactive = false) =>
    api.get('/content-groups', { params: includeInactive ? { include_inactive: true } : {} }),
  getById: (id) => api.get(`/content-groups/${id}`),
  create: (groupData) => api.post('/content-groups', groupData),
  update: (id, groupData) => api.put(`/content-groups/${id}`, groupData),
}

// Information Sources API
export const informationSourcesAPI = {
  getAll: (filters = {}) => api.get('/information-sources', { params: filters }),
  getById: (id) => api.get(`/information-sources/${id}`),
  create: (sourceData) => api.post('/information-sources', sourceData),
  update: (id, sourceData) => api.put(`/information-sources/${id}`, sourceData),
}

// QOL Concerns API
export const qolConcernsAPI = {
  getAll: (filters = {}) => api.get('/qol-concerns', { params: filters }),
  getById: (id) => api.get(`/qol-concerns/${id}`),
  create: (concernData) => api.post('/qol-concerns', concernData),
  update: (id, concernData) => api.put(`/qol-concerns/${id}`, concernData),
}

// Resources API
export const resourcesAPI = {
  getAll: (filters = {}) => api.get('/resources', { params: filters }),
  getById: (id) => api.get(`/resources/${id}`),
  create: (resourceData) => api.post('/resources', resourceData),
  update: (id, resourceData) => api.put(`/resources/${id}`, resourceData),
  // Distinct themes with active-resource counts: [{ theme, count }].
  // groupIds scopes the counts to a pathway's content groups (repeated
  // ?group_ids= params, as the route parses them); empty = all groups.
  getThemes: (groupIds = []) => {
    const params = new URLSearchParams()
    groupIds.forEach((id) => params.append('group_ids', String(id)))
    return api.get('/resources/themes', { params }).then(res => res.data.themes || [])
  },
}

// ===========================================
// Education Module APIs
// ===========================================

// Education Modules API (Published modules)
export const educationModulesAPI = {
  getAll: (filters = {}) => api.get('/education/modules', { params: filters }).then(res => res.data),
  getById: (moduleId) => api.get(`/education/modules/${moduleId}`).then(res => res.data),
  update: (moduleId, data) => api.put(`/education/modules/${moduleId}`, data).then(res => res.data),
  delete: (moduleId) => api.delete(`/education/modules/${moduleId}`).then(res => res.data),
  publish: (moduleId) => api.post(`/education/modules/${moduleId}/publish`).then(res => res.data),
}

// Education Drafts API (Module builder workflow)
export const educationDraftsAPI = {
  // Draft CRUD
  create: () => api.post('/education/drafts').then(res => res.data),
  getAll: (filters = {}) => api.get('/education/drafts', { params: filters }).then(res => res.data),
  getById: (draftId) => api.get(`/education/drafts/${draftId}`).then(res => res.data),
  delete: (draftId) => api.delete(`/education/drafts/${draftId}`).then(res => res.data),

  // Status polling
  getStatus: (draftId) => api.get(`/education/drafts/${draftId}/status`).then(res => res.data),

  // File operations
  uploadFiles: (draftId, files, onProgress) => {
    const formData = new FormData()
    files.forEach(file => formData.append('files', file))
    return api.post(`/education/drafts/${draftId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
      timeout: 300000 // 5 minute timeout for large uploads
    }).then(res => res.data)
  },
  removeFile: (draftId, fileName) =>
    api.delete(`/education/drafts/${draftId}/files/${encodeURIComponent(fileName)}`).then(res => res.data),

  // Analysis
  startAnalysis: (draftId) => api.post(`/education/drafts/${draftId}/analyze`).then(res => res.data),

  // YAML editing
  saveYaml: (draftId, yamlContent) =>
    api.put(`/education/drafts/${draftId}/yaml`, { yaml: yamlContent }).then(res => res.data),
  validateYaml: (draftId) => api.post(`/education/drafts/${draftId}/validate`).then(res => res.data),

  // Ingestion
  startIngestion: (draftId) => api.post(`/education/drafts/${draftId}/ingest`).then(res => res.data),

  // Publishing
  publish: (draftId) => api.post(`/education/drafts/${draftId}/publish`).then(res => res.data),
}

export default api 